/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Menu,
  MenuItem,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import Papa from 'papaparse';
import { getApi, makeRequest } from '@ink-ai/portal/common/requestHelper';
import { app } from '@ink-ai/portal/reducers/app';
import { GlossaryCollectionTable } from '../components/glossary/GlossaryCollectionTable';
import {
  glossaryActions,
  exportGlossaryItemsToCsv,
  exportGlossaryItemsToJSON,
  deleteGlossaryCollections,
  handleDownload,
  listGlossaryCollections,
} from '../../reducers/glossary';
import {
  GlossaryApi,
  GlossaryCollectionDto,
  GlossaryCollectionDtoStatusEnum,
  GlossaryCollectionDtoTypeEnum,
} from '@ink-ai/insight-service-sdk';
import { RootState } from '@ink-ai/portal/reducers';
import { LoadingButton } from '@mui/lab';
import { PaperCard } from '../components/Common';
import { DirectoryMoveDialog } from '../components/reference-store/DirectoryMoveDialog';
import { hasAnyRole, adminRolesForEnv, superAdminRolesForEnv, isDefaultProtectedName } from '@ink-ai/portal/common/authHelper';

export const Glossary: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const glossary = useSelector((state: RootState) => state.glossary);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state: RootState) => state.auth);
  const isAdmin = hasAnyRole(auth.group, adminRolesForEnv());
  const isSuperAdmin = hasAnyRole(auth.group, superAdminRolesForEnv());
  const [addingFolder, setAddingFolder] = useState(false);
  const [draftFolderName, setDraftFolderName] = useState('');
  const isGlossaryManagement = location.pathname === '/glossary-management';
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGlossaries, setSelectedGlossaries] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuLoading, setMenuLoading] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetIds, setMoveTargetIds] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  // 删除上下文：区分单行删除与批量删除
  const [deleteContext, setDeleteContext] = useState<'row' | 'bulk'>('bulk');
  // 文件夹删除确认
  const [confirmDeleteFolderOpen, setConfirmDeleteFolderOpen] = useState(false);
  const [pendingDeleteFolderId, setPendingDeleteFolderId] = useState<string | null>(null);
  const [pendingDeleteFolderName, setPendingDeleteFolderName] = useState<string>('');
  // Explorer 列表（包含文件夹与术语库）
  const [explorerItems, setExplorerItems] = useState<any[]>([]);
  const [explorerTotal, setExplorerTotal] = useState<number>(0);
  // 当前浏览的文件夹路径栈（从根到当前）
  const [folderStack, setFolderStack] = useState<Array<{ id: string; name?: string; parentId?: string | null }>>([]);
  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null;
  // "default"-protected rule (front-end mirror of the server guard): a
  // non-super-admin can't create/edit/delete a collection/folder whose name
  // contains "default", nor anything inside a default-named folder.
  const currentFolderName = folderStack.length > 0 ? folderStack[folderStack.length - 1].name : undefined;
  const currentFolderProtected = isDefaultProtectedName(currentFolderName);
  // Can the current user create new items in the current location?
  const canCreateHere = isSuperAdmin || !currentFolderProtected;
  // Per-row mutability gate passed to the table.
  const canMutateRow = React.useCallback(
    (row: any) => isSuperAdmin || (!isDefaultProtectedName(row?.name) && !currentFolderProtected),
    [isSuperAdmin, currentFolderProtected],
  );
  // 调试：跟踪 currentFolderId 与 folderStack 的变化
  useEffect(() => {
    console.log('[Glossary] currentFolderId-change', { currentFolderId, folderStack });
  }, [currentFolderId, folderStack]);
  const pendingDeleteNames = React.useMemo(() => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) return [] as string[];
    return (
      glossary.glossaries
        .filter((item) => pendingDeleteIds.includes(item.uuid))
        .map((item) => item.name) ?? []
    );
  }, [pendingDeleteIds, glossary.glossaries]);
  const pendingDeleteFolderNames = React.useMemo(() => {
    if (!selectedFolders || selectedFolders.length === 0) return [] as string[];
    return explorerItems
      .filter(
        (it: any) =>
          String(it.itemType || it.type || '').toLowerCase() === 'folder' &&
          selectedFolders.includes(it.uuid),
      )
      .map((it: any) => it.name);
  }, [selectedFolders, explorerItems]);
  
  // 加载 explorer 列表（根路径，支持搜索与分页）
  const loadExplorer = async () => {
    console.log('GlossaryCollections: loadExplorer called');
    try {
      dispatch(glossaryActions.setLoading(true));
      const params: Record<string, any> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm && searchTerm.trim().length > 0) {
        params.name = searchTerm.trim();
      }
      if (currentFolderId) {
        params.parentId = currentFolderId;
      }
      const data: any = await makeRequest({
        method: 'GET',
        url: '/api/v1/glossary/glossaryExplorer',
        params,
      });
      // 新版 API：返回键改为 collections，条目使用 itemType 指示 folder/file；兼容旧版 items。
      const rawItems =
        data.collections ||
        data.data?.collections ||
        data.items ||
        data.data?.items ||
        [];
      // 统一时间戳为毫秒，支持 number/ISO 字符串/空值
      const toMillis = (v: any) => {
        if (v == null) return undefined as any;
        if (typeof v === 'number') return v;
        return new Date(v).getTime();
      };
      const statusMap: Record<string, GlossaryCollectionDtoStatusEnum> = {
        READY: GlossaryCollectionDtoStatusEnum.Ready,
        IMPORTING: GlossaryCollectionDtoStatusEnum.Importing,
        PENDING_UPLOAD: GlossaryCollectionDtoStatusEnum.PendingUpload,
        ERROR: GlossaryCollectionDtoStatusEnum.Error,
      };
      const items = rawItems.map((it: any) => {
        const itemType = String(it.itemType || it.type || '').toLowerCase();
        const isFolder = itemType === 'folder';
        const isGlossary = itemType === 'file' || itemType === 'glossary';
        const createdAt = toMillis(it.createdAt);
        const updatedAt = toMillis(it.updatedAt);

        if (isFolder) {
          return {
            uuid: it.uuid,
            name: it.name,
            type: 'folder',
            desc: undefined,
            status: undefined,
            createdAt,
            updatedAt,
            itemCount:
              typeof it.count === 'number'
                ? it.count
                : Array.isArray(it.collections)
                ? it.collections.length
                : undefined,
            parentId: it.parentId ?? null,
          };
        }

        // 术语库（文件）
        return {
          uuid: it.uuid,
          name: it.name,
          // 保留后端原始类型（如 EXTERNAL），用于详情页分支判断
          type: it.type,
          desc: it.desc,
          // 传递错误信息以便在列表中展示 Tooltip
          errorMessage: it.errorMessage,
          // 保留外部词汇表的存储信息，供详情页显示 Index name 等
          externalGlossary: it.externalGlossary,
          status:
            statusMap[String((it.status || 'READY')).toUpperCase()] ?? it.status,
          createdAt,
          updatedAt,
          itemCount: it.itemCount ?? it.itemsCount ?? 0,
          parentId: it.parentId ?? currentFolderId ?? null,
        };
      });
      // 调试：打印映射后的示例及 parentId 覆盖情况
      try {
        const sample = items.slice(0, 5).map((it: any) => ({ uuid: it.uuid, type: it.type, parentId: it.parentId }));
        console.log('[Glossary] mapped-sample', { currentFolderId, sample });
      } catch {}
      const total = data.totalCount ?? data.data?.totalCount ?? items.length;
      setExplorerItems(items);
      setExplorerTotal(total);
    } catch (e) {
      console.error('Load glossary explorer failed:', e);
    } finally {
      dispatch(glossaryActions.setLoading(false));
    }
  };

  useEffect(() => {
    loadExplorer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, currentFolderId]);
  
  const hasReadyFileSelected = React.useMemo(() => {
    if (!selectedGlossaries || selectedGlossaries.length === 0) return false;
    const readyIds = new Set(
      explorerItems
        .filter((it: any) => String(it.type || '').toLowerCase() !== 'folder' && it.status !== GlossaryCollectionDtoStatusEnum.Importing)
        .map((it: any) => it.uuid)
    );
    return selectedGlossaries.some((id) => readyIds.has(id));
  }, [explorerItems, selectedGlossaries]);

  const handleRowDownload = async (row: GlossaryCollectionDto) => {
    setMenuLoading(true);
    try {
      if (row.type === GlossaryCollectionDtoTypeEnum.External) {
        await handleDownload(row.uuid, row.name);
      } else {
        await dispatch(exportGlossaryItemsToCsv(row.uuid));
      }
    } catch (error) {
      console.error('Error downloading glossary:', error);
    } finally {
      setMenuLoading(false);
    }
  };

  const handleRowDelete = async (row: GlossaryCollectionDto) => {
    if (menuLoading) return;
    console.log('[Glossary] row-delete click', { id: row.uuid, name: row.name });
    // 修复：行内删除仅针对单行，不应受已勾选文件夹的影响
    // setSelectedFolders([]);
    setDeleteContext('row');
    setPendingDeleteIds([row.uuid]);
    setConfirmDeleteOpen(true);
  };

  // 请求删除文件夹 -> 弹出确认框
  const handleFolderDelete = (folderUuid: string) => {
    if (menuLoading) return;
    const name = explorerItems.find((it: any) => it.type === 'folder' && it.uuid === folderUuid)?.name || '';
    setPendingDeleteFolderId(folderUuid);
    setPendingDeleteFolderName(name);
    setConfirmDeleteFolderOpen(true);
  };

  // 确认删除文件夹
  const handleConfirmDeleteFolder = async () => {
    if (!pendingDeleteFolderId) return;
    setMenuLoading(true);
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/api/v1/glossary/folders/${pendingDeleteFolderId}`,
        params: { cascade: true, force: true },
      });
      // 刷新 explorer 列表
      await loadExplorer();
    } catch (error) {
      console.error('Error deleting folder:', error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteFolderOpen(false);
      setPendingDeleteFolderId(null);
      setPendingDeleteFolderName('');
    }
  };

  const handleRowUpdate = (row: GlossaryCollectionDto) => {
    if (row.status === GlossaryCollectionDtoStatusEnum.Importing) return;
    navigate('/glossary-management/import', {
      state: {
        glossaryUuid: row.uuid,
        glossaryName: row.name,
        glossaryDescription: row.desc,
        glossaryType: row.type,
      },
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    if ((selectedGlossaries.length === 0 
      && selectedFolders.length === 0
    ) || menuLoading) return;
    console.log('[Glossary] bulk-delete menu click', {
      selectedItemIds: selectedGlossaries,
      selectedFolderIds: selectedFolders,
    });
    setDeleteContext('bulk');
    setPendingDeleteIds(selectedGlossaries);
    setConfirmDeleteOpen(true);
  };

  const handleExport = () => {
    handleMenuClose();
    if (selectedGlossaries.length === 1) {
      dispatch(exportGlossaryItemsToJSON(selectedGlossaries[0]));
    }
  };

  const handleExportCsv = async () => {
    setMenuLoading(true);
    try {
      // 辅助：获取某文件夹下的所有文件（术语库）ID
      const fetchFolderFileIds = async (folderId: string): Promise<string[]> => {
        const ids: string[] = [];
        let pageIdx = 1;
        const pageSize = 1000;
        while (true) {
          const data: any = await makeRequest({
            method: 'GET',
            url: '/api/v1/glossary/glossaryExplorer',
            params: { parentId: folderId, page: pageIdx, pageSize },
          });
          const raw = data.collections || data.data?.collections || data.items || data.data?.items || [];
          const files = raw.filter((it: any) => {
            const t = String(it.itemType || it.type || '').toLowerCase();
            const s = String(it.status || '').toUpperCase();
            return (t === 'file' || t === 'glossary') && s !== 'IMPORTING';
          });
          ids.push(...files.map((f: any) => f.uuid));
          const total = data.total ?? data.data?.total ?? raw.length;
          if (pageIdx * pageSize >= total || files.length === 0) break;
          pageIdx += 1;
        }
        return Array.from(new Set(ids));
      };

      // 只有选择了文件（未选文件夹）：基于选中 ID 执行下载/导出
      if (selectedFolders.length === 0) {
        const ids = selectedGlossaries;
        if (ids.length <= 1) {
          // 单条：根据元信息判断外部/内部并执行
          const id = ids[0];
          if (!id) {
            console.warn('[Glossary] no selected file id, skip');
            return;
          }
          const glossaryApi = await getApi(GlossaryApi);
          try {
            const metaResp = await glossaryApi.getGlossaryById(id);
            const meta = metaResp.data as any;
            const type = String(meta?.type || '').toLowerCase();
            const statusStr = String(meta?.status || '').toUpperCase();
            const name = meta?.name || '';
            if (statusStr === 'IMPORTING') {
              dispatch(app.actions.setGlobalMessage({ message: t('glossary.status.importing'), status: 'error' }));
              return;
            }
            if (type === 'external') {
              await handleDownload(id, name);
            } else {
              await dispatch(exportGlossaryItemsToCsv(id));
            }
          } catch (e) {
            console.error('Fetch glossary meta failed:', e);
          }
        } else {
          // 多条：与文件夹场景保持一致，逐条构建 ZIP
          const zip = new JSZip();
          const glossaryApi = await getApi(GlossaryApi);
          const usedNames = new Set<string>();

          const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').replace(/[\r\n\t]/g, '').trim();
          const makeUnique = (name: string) => {
            if (!usedNames.has(name)) {
              usedNames.add(name);
              return name;
            }
            let i = 1;
            let candidate = `${name} (${i})`;
            while (usedNames.has(candidate)) {
              i += 1;
              candidate = `${name} (${i})`;
            }
            usedNames.add(candidate);
            return candidate;
          };
          const extFromMime = (mime: string) => {
            const m = (mime || '').toLowerCase();
            const map: Record<string, string> = {
              'text/csv': '.csv',
              'application/json': '.json',
              'text/plain': '.txt',
              'application/pdf': '.pdf',
              'application/vnd.ms-excel': '.xls',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
            };
            return map[m] || '';
          };

          for (const id of ids) {
            try {
              const metaResp = await glossaryApi.getGlossaryById(id);
              const meta = metaResp.data as any;
              const type = String(meta?.type || '').toLowerCase();
              const statusStr = String(meta?.status || '').toUpperCase();
              if (statusStr === 'IMPORTING') {
                continue;
              }
              if (type === 'external') {
                const linkResp = await glossaryApi.getGlossaryDownloadLink(id);
                const url = linkResp.data.downloadLink;
                const res = await fetch(url);
                const buf = await res.arrayBuffer();
                const cd = res.headers.get('content-disposition') || '';
                const filenameMatch = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
                const fromHeader = filenameMatch?.[1] || filenameMatch?.[2] || '';
                let fromUrl = '';
                try {
                  const u = new URL(url);
                  fromUrl = decodeURIComponent(u.pathname.substring(u.pathname.lastIndexOf('/') + 1));
                } catch {}
                const extSource = fromHeader || fromUrl;
                let ext = '';
                if (extSource && extSource.includes('.')) {
                  ext = extSource.substring(extSource.lastIndexOf('.'));
                }
                if (!ext) {
                  const contentType = res.headers.get('content-type') || '';
                  ext = extFromMime(contentType);
                }
                let base = sanitize(meta?.name || fromHeader || fromUrl || id);
                if (ext && !base.toLowerCase().endsWith(ext.toLowerCase())) {
                  base += ext;
                }
                base = makeUnique(base);
                zip.file(base, buf);
              } else {
                const resp = await glossaryApi.glossaryControllerListGlossaryItemsById(id, '1', '2000', '');
                const items = (resp.data as any).items || [];
                const exportRows = items.map((item: any) => {
                  const { name, desc, abbr, translations } = item;
                  const cols = (translations || []).reduce((acc: Record<string, string>, t: any) => {
                    acc[t.language] = t.translation;
                    return acc;
                  }, {});
                  return { name, desc, abbr, ...cols };
                });
                const languages = Array.from(new Set(items.flatMap((it: any) => (it.translations || []).map((t: any) => t.language))));
                const headers = ['name', 'desc', 'abbr', ...languages];
                const csvData = [headers, ...exportRows.map((row) => headers.map((h) => (row as any)[h] || ''))];
                const csv = Papa.unparse(csvData, {
                  quotes: true,
                  quoteChar: '"',
                  escapeChar: '"',
                  delimiter: ',',
                  newline: '\n',
                });
                let base = sanitize(meta?.name || 'glossary');
                base = makeUnique(`${base}.csv`);
                zip.file(base, csv);
              }
            } catch (e) {
              console.error('Glossary zip build failed:', e);
            }
          }

          const blob = await zip.generateAsync({ type: 'blob' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'glossaries.zip';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } else {
        // 选择了文件夹（可能同时包含文件）：沿用原有逐文件打包 ZIP 方式
        const fromFolders = (await Promise.all(selectedFolders.map((id) => fetchFolderFileIds(id)))).flat();
        const allFileIds = Array.from(new Set([...(selectedGlossaries || []), ...fromFolders]));
        if (allFileIds.length === 0) return;

        const zip = new JSZip();
        const glossaryApi = await getApi(GlossaryApi);
        const usedNames = new Set<string>();

        const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').replace(/[\r\n\t]/g, '').trim();
        const makeUnique = (name: string) => {
          if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
          }
          let i = 1;
          let candidate = `${name} (${i})`;
          while (usedNames.has(candidate)) {
            i += 1;
            candidate = `${name} (${i})`;
          }
          usedNames.add(candidate);
          return candidate;
        };
        const extFromMime = (mime: string) => {
          const m = (mime || '').toLowerCase();
          const map: Record<string, string> = {
            'text/csv': '.csv',
            'application/json': '.json',
            'text/plain': '.txt',
            'application/pdf': '.pdf',
            'application/vnd.ms-excel': '.xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          };
          return map[m] || '';
        };

        for (const id of allFileIds) {
          try {
            // 获取术语库基本信息以判断类型
            const metaResp = await glossaryApi.getGlossaryById(id);
            const meta = metaResp.data as any;
            const type = String(meta?.type || '').toLowerCase();

            if (type === 'external') {
              // 外部术语库：获取下载链接后加入 ZIP
              const linkResp = await glossaryApi.getGlossaryDownloadLink(id);
              const url = linkResp.data.downloadLink;
              const res = await fetch(url);
              const buf = await res.arrayBuffer();
              const cd = res.headers.get('content-disposition') || '';
              const filenameMatch = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
              const fromHeader = filenameMatch?.[1] || filenameMatch?.[2] || '';
              let fromUrl = '';
              try {
                const u = new URL(url);
                fromUrl = decodeURIComponent(u.pathname.substring(u.pathname.lastIndexOf('/') + 1));
              } catch {}
              const extSource = fromHeader || fromUrl;
              let ext = '';
              if (extSource && extSource.includes('.')) {
                ext = extSource.substring(extSource.lastIndexOf('.'));
              }
              if (!ext) {
                const contentType = res.headers.get('content-type') || '';
                ext = extFromMime(contentType);
              }
              let base = sanitize(meta?.name || fromHeader || fromUrl || id);
              if (ext && !base.toLowerCase().endsWith(ext.toLowerCase())) {
                base += ext;
              }
              base = makeUnique(base);
              zip.file(base, buf);
            } else {
              // 内部术语库：导出 CSV 内容并写入到 ZIP
              const resp = await glossaryApi.glossaryControllerListGlossaryItemsById(id, '1', '2000', '');
              const items = (resp.data as any).items || [];
              const exportRows = items.map((item: any) => {
                const { name, desc, abbr, translations } = item;
                const cols = (translations || []).reduce((acc: Record<string, string>, t: any) => {
                  acc[t.language] = t.translation;
                  return acc;
                }, {});
                return { name, desc, abbr, ...cols };
              });
              const languages = Array.from(new Set(items.flatMap((it: any) => (it.translations || []).map((t: any) => t.language))));
              const headers = ['name', 'desc', 'abbr', ...languages];
              const csvData = [headers, ...exportRows.map((row) => headers.map((h) => (row as any)[h] || ''))];
              const csv = Papa.unparse(csvData, {
                quotes: true,
                quoteChar: '"',
                escapeChar: '"',
                delimiter: ',',
                newline: '\n',
              });
              let base = sanitize(meta?.name || 'glossary');
              base = makeUnique(`${base}.csv`);
              zip.file(base, csv);
            }
          } catch (e) {
            console.error('Glossary zip build failed:', e);
          }
        }

        const blob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'glossaries.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting glossaries:', error);
    } finally {
      setMenuLoading(false);
      handleMenuClose();
    }
  };

  const handleUpdate = () => {
    if (selectedGlossaries.length === 1) {
      const selId = selectedGlossaries[0];
      // 优先使用 explorerItems（与当前列表同步，包含 EXTERNAL 等类型）
      const selectedItem = explorerItems.find((it: any) => it.uuid === selId);
      const selectedGlossary = selectedItem || glossary.glossaries.find((g) => g.uuid === selId);
      if (selectedGlossary) {
        navigate('/glossary-management/import', {
          state: {
            glossaryUuid: selectedGlossary.uuid,
            glossaryName: selectedGlossary.name,
            glossaryDescription: selectedGlossary.desc,
            glossaryType: selectedGlossary.type,
          },
        });
      }
    }
    handleMenuClose();
  };

  const handleNavigate = (glossary: GlossaryCollectionDto) => {
    dispatch(glossaryActions.setSelectedGlossary(glossary));
    const targetPath = isGlossaryManagement
      ? `/glossary-management/${glossary.uuid}`
      : `/glossary/${glossary.uuid}`;
    navigate(targetPath, {
      state: {
        from: isGlossaryManagement ? '/glossary-management' : '/glossary',
      },
    });
  };

  const handleJsonExportDisabled = () => {
    if (selectedGlossaries.length !== 1) return true;
    const selectedGlossary = glossary.glossaries.find(
      (g) => g.uuid === selectedGlossaries[0],
    );
    return selectedGlossary?.type === GlossaryCollectionDtoTypeEnum.External;
  };

  const handleCsvExportDisabled = () => {
    return selectedGlossaries.length !== 1;
  };

  const handleUpdateDisabled = () => {
    if (selectedGlossaries.length !== 1) return true;
    const selId = selectedGlossaries[0];
    const selectedItem = explorerItems.find((it: any) => it.uuid === selId);
    const selectedGlossary = selectedItem || glossary.glossaries.find((g) => g.uuid === selId);
    return selectedGlossary?.status === GlossaryCollectionDtoStatusEnum.Importing;
  };

  const openMoveDialogFromSelection = () => {
    setMoveTargetIds(selectedGlossaries);
    setMoveDialogOpen(true);
    handleMenuClose();
  };

  const openMoveDialogFromRow = (row?: GlossaryCollectionDto) => {
    const ids = row ? [row.uuid] : selectedGlossaries;
    setMoveTargetIds(ids);
    setMoveDialogOpen(true);
  };

  // 进入某个文件夹
  const handleEnterFolder = (folder: { uuid: string; name?: string; parentId?: string | null }) => {
    // 进入文件夹时清空勾选状态
    setSelectedGlossaries([]);
    setSelectedFolders([]);
    // 进入文件夹时隐藏创建文件夹草稿行，避免在子目录创建
    setAddingFolder(false);
    setDraftFolderName('');
    console.log('[Glossary] enter-folder', folder);
    setFolderStack((prev) => [...prev, { id: folder.uuid, name: folder.name, parentId: folder.parentId ?? null }]);
    setPage(0);
  };

  // 返回上一级
  const handleBack = () => {
    if (folderStack.length === 0) return;
    // 返回上一级或根目录时清空勾选状态
    setSelectedGlossaries([]);
    setSelectedFolders([]);
    console.log('[Glossary] back-folder', { before: folderStack });
    setFolderStack((prev) => prev.slice(0, prev.length - 1));
    setPage(0);
  };

  const handleMoveCancel = () => {
    setMoveDialogOpen(false);
    setMoveTargetIds([]);
  };

  const handleMoveConfirm = async (folderId: string | null, itemIds: string[]) => {
    try {
      setMenuLoading(true);
      await makeRequest({
        method: 'POST',
        url: '/api/v1/glossary/move',
        data: { itemUuids: itemIds, folderId: folderId ?? null },
      });
      setMoveDialogOpen(false);
      // 刷新 explorer 列表
      await loadExplorer();
      setSelectedGlossaries([]);
      setSelectedFolders([]);
    } catch (e) {
      console.error('Move glossary items failed:', e);
    } finally {
      setMenuLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const isRow = deleteContext === 'row';
    const hasFiles = !!pendingDeleteIds && pendingDeleteIds.length > 0;
    const hasFolders = !isRow && selectedFolders.length > 0;
    console.log('[Glossary] confirm-delete open', {
      context: deleteContext,
      pendingDeleteIds,
      selectedFolderIds: selectedFolders,
    });
    if (!hasFiles && !hasFolders) {
      console.warn('[Glossary] nothing to delete, skip');
      return;
    }
    setMenuLoading(true);
    try {
      dispatch(glossaryActions.setLoading(true));
      if (hasFiles) {
        console.log('[Glossary] dispatch deleteGlossaryCollections', {
          count: pendingDeleteIds.length,
          ids: pendingDeleteIds,
        });
        const result = await dispatch(deleteGlossaryCollections(pendingDeleteIds));
        console.log('[Glossary] deleteGlossaryCollections completed', { result });
        setSelectedGlossaries((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)));
      }
      // 批量删除上下文才处理文件夹删除
      if (hasFolders) {
        try {
          console.log('[Glossary] batch-delete folders', {
            count: selectedFolders.length,
            ids: selectedFolders,
          });
          await makeRequest({
            method: 'POST',
            url: '/api/v1/glossary/folders/batch-delete',
            data: { items: selectedFolders, cascade: true, force: true },
          });
          console.log('[Glossary] batch-delete folders completed');
          setSelectedFolders([]);
        } catch (e) {
          console.error('Batch delete glossary folders failed:', e);
        }
      }
    } catch (error) {
      console.error('Error deleting glossary:', error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteOpen(false);
      console.log('[Glossary] confirm-delete closed, clear pending ids');
      setPendingDeleteIds([]);
      setDeleteContext('bulk');
      // 刷新 explorer 列表
      await loadExplorer();
      console.log('[Glossary] explorer reloaded after deletion');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('glossary.navigation.home')}
        </Link>
        {folderStack.length > 0 ? (
            <Link
              color="inherit"
              onClick={handleBack}
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              {isGlossaryManagement && isAdmin
                ? t('glossary.navigation.glossaryManagement')
                : t('glossary.navigation.glossary')}
            </Link>
        ) : (
          <Typography color="textPrimary">
            {isGlossaryManagement && isAdmin
              ? t('glossary.navigation.glossaryManagement')
              : t('glossary.navigation.glossary')}
          </Typography>
        )}
        {
          folderStack.length > 0 &&
          <Typography color="textPrimary">
            {folderStack[folderStack.length - 1]?.name}
          </Typography>
        }
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left', mb: 2 }}>
          {folderStack.length > 0
            ? (folderStack[folderStack.length - 1]?.name || t('glossary.navigation.glossary'))
            : (isGlossaryManagement && isAdmin
                ? t('glossary.navigation.glossaryManagement')
                : t('glossary.navigation.glossary'))}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            label={t('glossary.search.label')}
            variant="outlined"
            size="small"
            value={searchTerm}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleSearchChange}
          />
          <Box>
            {folderStack.length > 0 && (
              <Button
                sx={{ mr: 1 }}
                aria-label="back-folder"
                variant="outlined"
                onClick={handleBack}
              >
                <ArrowBackIcon />
              </Button>
            )}
            {
              isGlossaryManagement && isAdmin && !currentFolderId &&
              <Button
                sx={{ mr: 1 }}
                aria-label="add-folder"
                variant="outlined"
                onClick={() => {
                  setAddingFolder(true);
                  setDraftFolderName('');
                }}
              >
                <CreateNewFolderOutlinedIcon />
              </Button>
            }
            <Button
              sx={{ mr: 1 }}
              aria-label="refresh"
              variant="outlined"
              onClick={loadExplorer}
            >
              <RefreshIcon />
            </Button>
            {isGlossaryManagement && isAdmin && (
              <>
                <LoadingButton
                  ref={actionsButtonRef}
                  onClick={handleMenuOpen}
                  endIcon={<ArrowDropDownIcon />}
                  variant="outlined"
                  disabled={(selectedGlossaries.length === 0 && selectedFolders.length === 0) || addingFolder}
                  loading={menuLoading}
                >
                  {t('glossary.actions.button')}
                </LoadingButton>
                {canCreateHere && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ ml: 1 }}
                    onClick={() => navigate('/glossary-management/import')}
                  >
                    {t('glossary.actions.import')}
                  </Button>
                )}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  slotProps={{
                    paper: {
                      style: {
                        minWidth: actionsButtonRef.current?.offsetWidth,
                      },
                    },
                  }}
                >
                  {/* <MenuItem
                    onClick={handleExport}
                    disabled={handleJsonExportDisabled() || menuLoading}
                  >
                    {t('glossary.actions.exportJson')}
                  </MenuItem>
                  <MenuItem
                    onClick={handleExportCsv}
                    disabled={handleCsvExportDisabled() || menuLoading}
                  >
                    {t('glossary.actions.exportCsv')}
                  </MenuItem> */}
                  <MenuItem
                    onClick={handleExportCsv}
                    disabled={(selectedGlossaries.length === 0 && selectedFolders.length === 0) || menuLoading || (selectedFolders.length === 0 && selectedGlossaries.length > 0 && !hasReadyFileSelected)}
                  >
                    {t('glossary.actions.download')}
                  </MenuItem>
                  {/* <MenuItem
                    onClick={handleDelete}
                    disabled={(selectedGlossaries.length === 0 && selectedFolders.length === 0) || menuLoading}
                  >
                    {t('glossary.actions.delete')}
                  </MenuItem> */}
                  <MenuItem
                    onClick={openMoveDialogFromSelection}
                    disabled={(selectedGlossaries.length === 0) || menuLoading}
                  >
                    {t('glossary.actions.move')}
                  </MenuItem>
                  <MenuItem
                    onClick={handleUpdate}
                    disabled={handleUpdateDisabled() || menuLoading}
                  >
                    {t('glossary.actions.update')}
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={handleDelete}
                    disabled={(selectedGlossaries.length === 0 && selectedFolders.length === 0) || menuLoading}
                  >
                    <Typography color="error">
                      {t('glossary.actions.delete')}
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
        <GlossaryCollectionTable
          selectedGlossaries={selectedGlossaries}
          selectedFolders={selectedFolders}
          onSelectedFoldersChange={setSelectedFolders}
          onSelectedGlossariesChange={setSelectedGlossaries}
          searchTerm={searchTerm}
          onNavigate={handleNavigate}
          onEnterFolder={handleEnterFolder}
          onMoveClick={(row) => openMoveDialogFromRow(row)}
          onDownloadClick={handleRowDownload}
          onDeleteClick={handleRowDelete}
          onUpdateClick={handleRowUpdate}
          onDeleteFolderClick={handleFolderDelete}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          enableSelection={isGlossaryManagement && isAdmin}
          showActions={isGlossaryManagement && isAdmin}
          canMutateRow={canMutateRow}
          rowActionLoading={menuLoading}
          addingFolder={addingFolder}
          draftFolderName={draftFolderName}
          onDraftFolderNameChange={(name) => setDraftFolderName(name)}
          onConfirmAddFolder={() => {
            // 创建新文件夹仅限根目录
            (async () => {
              try {
                if (!draftFolderName || draftFolderName.trim().length === 0) {
                  setAddingFolder(false);
                  setDraftFolderName('');
                  return;
                }
                setMenuLoading(true);
                await makeRequest({
                  method: 'POST',
                  url: '/api/v1/glossary/folders',
                  data: { name: draftFolderName.trim(), parentId: null },
                });
                setAddingFolder(false);
                setDraftFolderName('');
                await loadExplorer();
              } catch (e) {
                console.error('Create glossary folder failed:', e);
                const msg = (e as any)?.response?.data?.message ?? (e as any)?.data?.message ?? (e as any)?.message ?? String(e);
                dispatch(app.actions.setGlobalMessage({ message: msg, status: 'error' }));
                setAddingFolder(true);
                setDraftFolderName(draftFolderName.trim());
              } finally {
                setMenuLoading(false);
              }
            })();
          }}
          onCancelAddFolder={() => {
            setAddingFolder(false);
            setDraftFolderName('');
          }}
          mixedItems={explorerItems}
          totalCount={explorerTotal}
          cascadeSelection
          currentFolderId={currentFolderId}
          onReloadExplorer={() => { console.log('GlossaryCollections: onReloadExplorer'); loadExplorer(); }}
        />
        <DirectoryMoveDialog
          open={moveDialogOpen}
          itemIds={moveTargetIds}
          onCancel={handleMoveCancel}
          onConfirm={handleMoveConfirm}
          fetchFolders={async (page, pageSize) => {
            try {
              // const p = Math.max(0, page) + 1; // API 是 1-based
              // const data: any = await makeRequest({
              //   method: 'GET',
              //   url: '/api/v1/glossary/glossaryExplorer',
              //   // 根目录：不传 parentId
              //   params: { page: p, pageSize },
              // });
              // const raw =
              //   data.collections ||
              //   data.data?.collections ||
              //   data.items ||
              //   data.data?.items ||
              //   [];
              // const folders = raw
              //   .filter((it: any) => String(it.itemType || it.type || '').toLowerCase() === 'folder')
              //   .map((f: any) => ({ id: f.uuid, name: f.name }));
              // return { total: data.total ?? data.data?.total ?? folders.length, items: folders };
              const p = Math.max(0, page) + 1; // API 是 1-based
              const data: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/glossary/folders',
                // 根目录：传递 parentId=null
                params: { page: p, pageSize },
              });
              const raw = data.folders || data.data?.folders || [];
              const folders = raw.map((f: any) => ({ id: f.uuid, name: f.name }));
              return { total: data.totalCount ?? data.total ?? data.data?.total ?? folders.length, items: folders };
            } catch (e) {
              console.error('Fetch glossary folders failed:', e);
              return { total: 0, items: [] };
            }
          }}
          fetchFiles={async (folderId, page, pageSize) => {
            try {
              const p = Math.max(0, page) + 1; // API 是 1-based
              const data: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/glossary/glossaryExplorer',
                params: { parentId: folderId, page: p, pageSize },
              });
              const raw =
                data.collections ||
                data.data?.collections ||
                data.items ||
                data.data?.items ||
                [];
              const files = raw
                .filter((it: any) => {
                  const t = String(it.itemType || it.type || '').toLowerCase();
                  return t === 'file' || t === 'glossary';
                })
                .map((f: any) => ({ id: f.uuid, name: f.name }));
              return { total: data.total ?? data.data?.total ?? files.length, items: files };
            } catch (e) {
              console.error('Fetch glossary files in folder failed:', e);
              return { total: 0, items: [] };
            }
          }}
        />
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>{t('glossary.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteContext === 'bulk' && selectedFolders.length > 0 && pendingDeleteIds.length > 0
                ? t('glossary.confirmDelete.messageMixed', { folderCount: selectedFolders.length, fileCount: pendingDeleteIds.length })
                : deleteContext === 'bulk' && selectedFolders.length > 0
                ? t('glossary.confirmDelete.messageFolders', { count: selectedFolders.length })
                : t('glossary.confirmDelete.messageFiles', { count: pendingDeleteIds.length })}
            </DialogContentText>
            {/* 注释掉所选文件夹与文件名称的展示，避免 pendingDeleteNames 问题影响使用 */}
            {/* {pendingDeleteFolderNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('glossary.confirmDelete.labels.folders')}{pendingDeleteFolderNames.join('、')}
              </Typography>
            )}
            {pendingDeleteNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('glossary.confirmDelete.labels.files')}{pendingDeleteNames.join('、')}
              </Typography>
            )} */}
            {deleteContext === 'bulk' && selectedFolders.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('glossary.confirmDeleteFolder.messageWithoutName')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              loading={menuLoading}
            >
              {t('glossary.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        {/* 文件夹删除确认 */}
        <Dialog
          open={confirmDeleteFolderOpen}
          onClose={() => {
            setConfirmDeleteFolderOpen(false);
            setPendingDeleteFolderId(null);
            setPendingDeleteFolderName('');
          }}
        >
          <DialogTitle>{t('glossary.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('glossary.confirmDeleteFolder.message', { name: pendingDeleteFolderName })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setConfirmDeleteFolderOpen(false);
              setPendingDeleteFolderId(null);
              setPendingDeleteFolderName('');
            }}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmDeleteFolder}
              loading={menuLoading}
            >
              {t('glossary.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </PaperCard>
    </Container>
  );
};
